/**
 * Create the Renewal Result Agent node
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail, renewalResultRequiredGuardrail } from '../guardrails';
import { searchFAQs, formatSearchResults } from '../../utils/faq-search';

export function createRenewalResultAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'renewal_result_agent',
    name: 'Renewal Result Agent',
    description: 'Step 4: Display renewal result',
    
    // Initial prompt to trigger AI behavior

    
    // Backend provides the actual system instruction via ephemeral token

    
    // Initial prompt to trigger the AI to announce the result
    // The actual result data is injected into system instruction by AgentManager
    initialPrompt: 'The renewal submission is complete. Look at the RENEWAL RESULT DATA section in your instructions above. If it says "ERROR: true", announce the ERROR MESSAGE to the user - read exactly what the error says. If it says "SUCCESS: true", announce the RequestId and guide user to click payment button. Do NOT make up a success message if it shows ERROR!',
    
    // Must have renewal result
    entryGuardrails: {
      required: [languageDetectedGuardrail, renewalResultRequiredGuardrail],
      optional: [],
    },
    
    exitGuardrails: {
      transitions: new Map([
        ['new_request', {
          target: { agentId: 'renewal_input_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['finish', {
          target: { agentId: 'router_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: { agentId: 'router_agent' },
    },
    
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      if (toolName === 'new_renewal_request') {
        console.log('[RenewalResultAgent] Starting new renewal request');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Starting new renewal' },
          triggersTransition: true,
          transitionTo: 'renewal_input_agent',
          transitionData: {
            // Clear previous renewal data
            reportData: undefined,
            selectedVehicle: undefined,
            renewalResult: undefined,
          },
        };
      }
      
      if (toolName === 'finish_renewal') {
        console.log('[RenewalResultAgent] Finishing renewal flow');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Returning to menu' },
          triggersTransition: true,
          transitionTo: 'router_agent',
          transitionData: {
            // Clear all renewal data
            reportData: undefined,
            selectedVehicle: undefined,
            renewalResult: undefined,
          },
        };
      }
      
      // Inline FAQ - answer questions without leaving
      if (toolName === 'search_faq') {
        const question = args.question || '';
        const searchResults = await searchFAQs(question, 3, 2);
        const language = context.transitionData.language || 'en';
        const formattedResults = formatSearchResults(searchResults, language);
        
        console.log('[RenewalResultAgent] Inline FAQ:', question);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            results_count: searchResults.length,
            results: formattedResults,
            message: 'Answer the question, then remind user of their options.',
          },
          triggersTransition: false,
        };
      }
      
      // Cross-flow navigation - Accident reporting
      if (toolName === 'start_accident_reporting') {
        console.log('[RenewalResultAgent] Cross-flow: Starting accident reporting');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Starting accident reporting' },
          triggersTransition: true,
          transitionTo: 'accident_start_agent',
          transitionData: {
            reportData: undefined,
            selectedVehicle: undefined,
            renewalResult: undefined,
          },
        };
      }
      
      // Cross-flow navigation - Lost report search
      if (toolName === 'start_lost_report_search') {
        console.log('[RenewalResultAgent] Cross-flow: Starting lost report search');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Starting lost report search' },
          triggersTransition: true,
          transitionTo: 'lost_report_upload_extract_agent',
          transitionData: {
            reportData: undefined,
            selectedVehicle: undefined,
            renewalResult: undefined,
          },
        };
      }
      
      // Cross-flow navigation - FAQ
      if (toolName === 'go_to_faq') {
        console.log('[RenewalResultAgent] Cross-flow: Going to FAQ');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Going to FAQ' },
          triggersTransition: true,
          transitionTo: 'faq_agent',
          transitionData: {
            reportData: undefined,
            selectedVehicle: undefined,
            renewalResult: undefined,
          },
        };
      }
      
      return {
        toolName,
        args,
        result: { success: false, error: 'Unknown tool' },
      };
    },
    
    onActivate: async (context: AgentContext) => {
      const result = context.transitionData.renewalResult;
      console.log('[RenewalResultAgent] Activated');
      console.log('  Success:', result?.success);
      console.log('  RequestId:', result?.RequestId);
    },
    
    onDeactivate: async (context: AgentContext) => {
      console.log('[RenewalResultAgent] Deactivated');
    },
  };
}

export default createRenewalResultAgent;

